import Decimal from "decimal.js"

const PRICE_PRECISION_DIVISOR = {
  cents: 100,
  hundredths_of_cents: 10000,
}

// products.price_per_unit is nullable and DryStack::LaunchAddonProductsQuery
// filters on availability and product type only, never on price — its own ORDER
// clause says NULLS LAST. So an unpriced product does reach the catalog, and
// every caller here has to get null back rather than a Decimal built from
// nothing, which throws and takes the whole surface down with it.
const unitPriceDollars = (product) => {
  if (product.pricePerUnit === null || product.pricePerUnit === undefined) {
    return null
  }

  return new Decimal(product.pricePerUnit).div(
    PRICE_PRECISION_DIVISOR[product.pricePrecision]
  )
}

// An unpriced product cannot be ordered: there is no amount to show a boater
// and nothing for the server to snapshot onto the line.
export const isPriced = (product) => unitPriceDollars(product) !== null

// The live figure beside the gallons field while a boater is still typing in it.
// Deliberately still local: it is one multiplication, it has to keep up with the
// keystroke, and a debounced round trip would leave a stale number under the
// cursor. Rounds the way LaunchAddon#subtotal does, so it cannot disagree with
// the server rows below it. Money that involves tax, wording, or a currency
// symbol comes from the server — see DryStack::LaunchAddonEstimateDecorator.
export const lineTotalCents = (product, quantity) => {
  const unitPrice = unitPriceDollars(product)
  if (unitPrice === null) return null

  return unitPrice.mul(quantity).mul(100).round().toNumber()
}

// LaunchAddon rejects a fuel line with no mode (validate_fuel_mode_matches_product_type)
// and a gallons line with no positive quantity, and DryStack::LaunchAddons::Replace
// rolls the entire launch back when it does — with :quantity outside its
// BOATER_SAFE_ERRORS, so the boater is told only that the item is unavailable.
// Both cases have to be caught before the request rather than after.
export const MIN_FUEL_GALLONS = 0.00001

export const fuelSelectionError = (product, selection) => {
  if (!product?.fuel) return null
  if (!selection.fuelMode) return "mode"

  if (
    selection.fuelMode === "gallons" &&
    !(selection.quantity >= MIN_FUEL_GALLONS)
  ) {
    return "gallons"
  }

  return null
}

// The one gate `Replace` won't relax for: a selection that reached this point
// missing from the catalog, unpriced, out of stock, or with an unresolved fuel
// choice all roll the *entire launch* back server-side (ScheduleAppointment.transaction
// wraps the launch and its add-ons in one write), most with no boater-safe
// message. Shared by the pre-submit guard and the submit payload so the two
// can't drift from what the server will actually accept.
export const isSubmittable = (product, selection) =>
  Boolean(product) &&
  isPriced(product) &&
  !product.outOfStock &&
  !fuelSelectionError(product, selection)

export const hasIncompleteAddOnSelection = (catalog, value) =>
  value.some(
    (selection) =>
      !isSubmittable(
        catalog.find((item) => item.productId === selection.productId),
        selection
      )
  )
